/**
 * Safe environment variable access for dev refresh and org-prefixed routes.
 * public/env.js sets window.env (loaded from index.html); react-dotenv may be undefined.
 * env.js must use an absolute path (/env.js) so nested routes like /org-slug/team still load it.
 */

const getReactDotenv = () => {
  try {
    // eslint-disable-next-line global-require
    const mod = require("react-dotenv");
    const env = mod?.default ?? mod;
    if (env && typeof env === "object" && !Array.isArray(env)) {
      return env;
    }
  } catch {
    // ignore
  }
  return null;
};

const readEnvKey = (key) => {
  if (
    typeof window !== "undefined" &&
    window.env &&
    typeof window.env === "object" &&
    window.env[key]
  ) {
    return window.env[key];
  }

  if (typeof process !== "undefined" && process.env?.[key]) {
    return process.env[key];
  }

  const dotenv = getReactDotenv();
  if (dotenv?.[key]) {
    return dotenv[key];
  }

  return null;
};

const LOCAL_BACKEND_DEFAULT = "http://localhost:8080/api";
const LOCAL_USERMGMT_DEFAULT = "http://localhost:8081";

const isLocalHost = () => {
  if (typeof window === "undefined" || !window.location?.hostname) {
    return true;
  }
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1";
};

export const getEnvVar = (key, defaultValue = null) => {
  let value = readEnvKey(key);

  if (!value && key === "REACT_APP_BACKEND_URL") {
    value = readEnvKey("REACT_APP_HMSBACKEND_URL");
  }

  if (value) {
    return value;
  }

  return defaultValue;
};

const resolveBackendUrl = () => {
  const configured = getEnvVar("REACT_APP_BACKEND_URL");
  if (configured) {
    return configured;
  }

  if (isLocalHost()) {
    return LOCAL_BACKEND_DEFAULT;
  }

  console.error(
    "[envConfig] REACT_APP_BACKEND_URL is missing. Ensure /env.js loads and Azure app settings are set."
  );
  return LOCAL_BACKEND_DEFAULT;
};

const resolveUsermgmtUrl = () => {
  const configured = getEnvVar("REACT_APP_USERMGMT_URL");
  if (configured) {
    return configured;
  }

  if (isLocalHost()) {
    return LOCAL_USERMGMT_DEFAULT;
  }

  console.error(
    "[envConfig] REACT_APP_USERMGMT_URL is missing. Ensure /env.js loads and Azure app settings are set."
  );
  return LOCAL_USERMGMT_DEFAULT;
};

export const BACKEND_URL = resolveBackendUrl();
export const USERMGMT_URL = resolveUsermgmtUrl();
export const GEMINI_API_KEY = getEnvVar("REACT_APP_GEMINI_API_KEY", "");
